import { memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Text, Popover, Button, Switch, useTheme } from '@Sage/hoi-poi-ui';

import { FLAGSMITH_FLAGS_TO_CONFIG } from 'constants/Features';

import {
    getAvailableDevEnvironments,
    getDevEnvironmentOverride,
    hasDevEnvironmentOverride,
    isDevEnvironmentSwitcherEnabled,
    setDevEnvironment,
} from './environments';
import {
    clearFlagOverrides,
    getFlagOverrides,
    hasFlagOverrides,
    setFlagOverride,
} from './flagsmithOverrides';
import './styles.scss';

const OverrideSparkles = () => (
    <>
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
    </>
);

const DevPopUp = memo(() => {
    const theme = useTheme();
    const flagsmith = useSelector((state) => state.config?.flagsmith || {});
    const overrideEnvironment = useMemo(() => getDevEnvironmentOverride(), []);
    const hasEnvOverride = useMemo(() => hasDevEnvironmentOverride(), []);
    const flagOverrides = useMemo(() => getFlagOverrides(), []);
    const hasFlagsOverride = useMemo(() => hasFlagOverrides(), []);

    const setEnvironment = useCallback(
        (name) => {
            // Clicking the active override deselects it and returns to the deploy default
            if (overrideEnvironment && name === overrideEnvironment) {
                setDevEnvironment('');
                window.location.reload();
                return;
            }

            setDevEnvironment(name);
            window.location.reload();
        },
        [overrideEnvironment],
    );

    const handleFlagChange = useCallback((key, checked) => {
        setFlagOverride(key, checked);
        window.location.reload();
    }, []);

    const handleClearFlagOverrides = useCallback(() => {
        clearFlagOverrides();
        window.location.reload();
    }, []);

    const EnvironmentsContent = useMemo(() => {
        return (
            <div className="fm-dev__popup__buttons">
                {getAvailableDevEnvironments().map((env) => (
                    <Button
                        key={env.name}
                        size="small"
                        type={env.name === overrideEnvironment ? 'primary' : 'secondary'}
                        onClick={() => setEnvironment(env.name)}
                    >
                        {env.name}
                    </Button>
                ))}
            </div>
        );
    }, [overrideEnvironment, setEnvironment]);

    const FlagsContent = useMemo(() => {
        return (
            <div className="fm-dev__popup__flags">
                <div className="fm-dev__popup__flags__list">
                    {FLAGSMITH_FLAGS_TO_CONFIG.map((flag) => {
                        const isOverridden = Object.prototype.hasOwnProperty.call(
                            flagOverrides,
                            flag,
                        );
                        return (
                            <div
                                key={flag}
                                className={`fm-dev__popup__flags__row${
                                    isOverridden ? ' fm-dev__popup__flags__row--override' : ''
                                }`}
                            >
                                <Text type="caption">{flag}</Text>
                                <Switch
                                    size="small"
                                    checked={Boolean(flagsmith[flag])}
                                    onChange={(checked) => handleFlagChange(flag, checked)}
                                />
                            </div>
                        );
                    })}
                </div>
                <Button
                    size="small"
                    type="secondary"
                    isDisabled={!hasFlagsOverride}
                    onClick={handleClearFlagOverrides}
                >
                    Clear overrides
                </Button>
            </div>
        );
    }, [flagOverrides, flagsmith, handleClearFlagOverrides, handleFlagChange, hasFlagsOverride]);

    if (!isDevEnvironmentSwitcherEnabled()) return null;

    return (
        <div className="fm-dev__popup">
            <Popover placement="top" content={EnvironmentsContent}>
                <div
                    className={`fm-dev__popup__tag${
                        hasEnvOverride ? ' fm-dev__popup__tag--override' : ''
                    }`}
                >
                    {hasEnvOverride && <OverrideSparkles />}
                    <Text color={theme.colors.primary.white}>ENVIRONMENTS</Text>
                </div>
            </Popover>
            <Popover placement="top" content={FlagsContent}>
                <div
                    className={`fm-dev__popup__tag${
                        hasFlagsOverride ? ' fm-dev__popup__tag--override' : ''
                    }`}
                >
                    {hasFlagsOverride && <OverrideSparkles />}
                    <Text color={theme.colors.primary.white}>FLAGS</Text>
                </div>
            </Popover>
        </div>
    );
});

DevPopUp.displayName = 'DevPopUp';

export default memo(DevPopUp);
